import { Button, Dialog, Text, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { LinkedAccountCard } from '@/pages/FarmerList/LinkedAccountCard';
import { UserApi } from '@/utilities/api/UserApi';
import { sortLinkedAccounts } from '@/utilities/Farmer';
import { ApiUser } from '@api/interfaces';
import { Divider, Stack } from '@mui/material';
import { getAccountName } from '@shared/utilities';
import React, { Fragment, useMemo } from 'react';
import { useQuery } from 'react-query';
import { AccountGroupRole } from "@shared/enums";
import { AboutAccountRolesDialog } from "@/components/shared/AboutAccountRolesDialog";
import { useTranslation } from 'react-i18next';

interface LinkedAccountsDialogProps {
  brandColor?: string,
  customer: ApiUser,
  onClose: () => void,
  onViewAccount?: (customer: ApiUser) => void,
  show: boolean,
}

export const LinkedAccountsDialog = ({
  brandColor,
  customer,
  onClose,
  onViewAccount,
  show,
}: LinkedAccountsDialogProps) => {
  const { t } = useTranslation();
  const [showAboutRolesModal, setShowAboutRolesModal] = React.useState(false);

  const { data: linkedAccounts } = useQuery(
    [QueryKeys.GET_LINKED_ACCOUNTS, customer.id],
    () => UserApi.getLinkedAccounts(customer.id),
  );

  const sortedAccounts = useMemo(() => sortLinkedAccounts(linkedAccounts ?? []), [linkedAccounts]);

  const partnerAccounts = sortedAccounts.filter(({ accountGroupRole }) => (
    accountGroupRole === AccountGroupRole.Partner
  ));
  const independentPartnerAccounts = sortedAccounts.filter(({ accountGroupRole }) => (
    accountGroupRole === AccountGroupRole.IndependentPartner
  ));
  const primaryAccounts = sortedAccounts.filter(({ accountGroupRole }) => (
    accountGroupRole === AccountGroupRole.Primary
  ));
  const secondaryAccounts = sortedAccounts.filter(({ accountGroupRole }) => (
    accountGroupRole === AccountGroupRole.Secondary
  ));

  const AccountGroupSection = ({
    accountGroup,
    subtitle,
    title,
  }: {
    accountGroup: ApiUser[],
    subtitle: string,
    title: string,
  }) => {
    return (
      <Stack>
        <VSpacer size="4" />
        <Text category="h6" fontWeight={500}>
          {title}
        </Text>
        <Text category="body-small">
          {subtitle}
        </Text>
        <VSpacer size="4" />
        <Divider />
        {accountGroup.map((account) => (
          <Fragment key={account.id}>
            <LinkedAccountCard
              account={account}
              onViewAccount={onViewAccount}
            />
            <Divider />
          </Fragment>
        ))}
        <VSpacer size="6" />
      </Stack>
    );
  };

  return (
    <Dialog
      acceptButton={(props) => (
        <Button
          {...props}
          onClick={onClose}
          testID="close-linked-accounts-dialog-button"
          variant="contained"
        >
          {t($ => $.common.actions.close)}
        </Button>
      )}
      onClose={onClose}
      open={show}
      secondaryActionButton={(props) => (
        <Button
          {...props}
          onClick={() => setShowAboutRolesModal(true)}
          testID="close-linked-accounts-dialog-button"
          variant="outlined"
        >
          {t($ => $.customers.linkedAccounts.aboutAccountRoles)}
        </Button>
      )}
      showCloseButton={false}
      subtitle={(
        <Stack direction="row">
          <Text category="body-medium">
            <b>{t($ => $.common.filters.account)}:</b> {getAccountName(customer)}
          </Text>
        </Stack>
      )}
      testID="linked-accounts-dialog"
      title={t($ => $.customers.linkedAccounts.title)}
    >
      {!sortedAccounts.length && (
        <>
          <VSpacer size="5" />
          <Text>{t($ => $.customers.linkedAccounts.noLinkedAccounts)}</Text>
        </>
      )}
      {!!partnerAccounts.length && (
        <AccountGroupSection
          accountGroup={partnerAccounts}
          subtitle={t($ => $.customers.linkedAccounts.partnerDescription)}
          title={t($ => $.customers.linkedAccounts.partnerAccounts)}
        />
      )}
      {!!independentPartnerAccounts.length && (
        <AccountGroupSection
          accountGroup={independentPartnerAccounts}
          subtitle={t($ => $.customers.linkedAccounts.independentPartnerDescription)}
          title={t($ => $.customers.linkedAccounts.independentPartnerAccounts)}
        />
      )}
      {!!primaryAccounts.length && (
        <AccountGroupSection
          accountGroup={primaryAccounts}
          subtitle={t($ => $.customers.linkedAccounts.primaryDescription)}
          title={t($ => $.customers.linkedAccounts.primaryAccounts)}
        />
      )}
      {!!secondaryAccounts.length && (
        <AccountGroupSection
          accountGroup={secondaryAccounts}
          subtitle={t($ => $.customers.linkedAccounts.secondaryDescription)}
          title={t($ => $.customers.linkedAccounts.secondaryAccounts)}
        />
      )}
      {showAboutRolesModal && !!customer.accountGroupRole && !!linkedAccounts?.length && (
        <AboutAccountRolesDialog
          brandColor={brandColor}
          customerRole={customer.accountGroupRole}
          linkedAccounts={linkedAccounts}
          onClose={ () => setShowAboutRolesModal(false) }
          pageType="rp"
        />
      )}
    </Dialog>
  );
};
